import { useEffect, useState, useSyncExternalStore } from 'react';
import { apiFetch } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { chatReferenceEpoch, resetChatReferenceViews, subscribeChatReferenceEpoch } from '../../lib/chat-references.ts';

/** No shared response cache; identity+scope+epoch guards late responses, even after an aborted token lookup. */
export function useChatReferenceData<T>(scope: string, path: string | null, body?: unknown) {
  const { me, getToken, auth } = useSession();
  useEffect(() => auth.onSignedOut(resetChatReferenceViews), [auth]);
  const epoch = useSyncExternalStore(subscribeChatReferenceEpoch, chatReferenceEpoch);
  const serialized = body === undefined ? '' : JSON.stringify(body);
  const key = `${me.id}:${scope}:${path}:${serialized}:${epoch}`;
  const [state, setState] = useState<{ key: string; data?: T; error?: boolean }>({ key: '' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!path) return;
    const controller = new AbortController();
    void apiFetch<T>(getToken, path, { signal: controller.signal, ...(serialized ? { method: 'POST', body: JSON.parse(serialized) as unknown } : {}) })
      .then((data) => { if (!controller.signal.aborted) setState({ key, data }); })
      .catch(() => { if (!controller.signal.aborted) setState({ key, error: true }); });
    return () => controller.abort();
  }, [key, path, serialized, getToken, attempt]);
  return { data: state.key === key ? state.data : undefined, error: state.key === key && !!state.error, loading: !!path && state.key !== key, retry: () => { setState({ key: '' }); setAttempt((n) => n + 1); } };
}
